import { useUserStore } from "@/stores/userStore";
import { useDeviceStore } from "@/stores/deviceStore";
import { Block, BlockType, USE_RESTRICTED_CHECK_TIME_MILLIS } from "@/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { computed, ref, watch } from "vue";
import { useServiceStore } from "@/stores/serviceStore";

dayjs.locale("ko");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Seoul");

export function useUseBlockChecker(blockType: BlockType) {
  const userStore = useUserStore();
  const deviceStore = useDeviceStore();
  const serviceStore = useServiceStore();

  const isApplicationActive = computed(() => deviceStore.applicationIsActive);

  const blockEndDtm = computed(
    () => userStore.userInfo?.[`${blockType}EndDtm`]
  );

  /** 일시까지 보여주는지 여부 (false: 일까지만 표시, true: 일시까지만 표시) */
  const displayBlockDtmYn = computed(
    () => serviceStore.serviceInfo?.displayBlockDtmYn
  );

  const getFormattedHour = () => {
    if (!blockEndDtm.value?.length) return "";
    return dayjs(blockEndDtm.value).hour() >= 12 ? "오후" : "오전";
  };

  /** 현재 시간을 기준으로 이용제한 상태인지 확인한다 */
  const isBeforeBlockEnd = () => {
    return dayjs().isBefore(dayjs(blockEndDtm.value));
  };

  /** 이용제한 여부 */
  const useRestricted = ref(!!blockEndDtm.value?.length && isBeforeBlockEnd());

  /** 이용기간 제한 해제 날짜 문구 */
  const formatBlockReleaseDateTime = () => {
    // 이용기간 제한 해제 일시는 만료 일시에 1초를 더한 값이다.
    const date = dayjs(blockEndDtm.value).add(1, "seconds");

    let formatString = `YYYY년 M월 D일`;
    if (displayBlockDtmYn.value) {
      formatString += ` ${getFormattedHour()} h시 m분 s초`;
    }

    return date.format(formatString);
  };

  /** 이용기간 제한 해제 날짜 문구 업데이트 */
  const updateBlockReleaseDisplay = () => {
    if (!blockEndDtm.value) {
      blockType === Block.BOOK
        ? userStore.setBookBlockReleaseDisplay("")
        : userStore.setBoardBlockReleaseDisplay("");
      return;
    }

    const formattedDate = formatBlockReleaseDateTime();
    blockType === Block.BOOK
      ? userStore.setBookBlockReleaseDisplay(formattedDate)
      : userStore.setBoardBlockReleaseDisplay(formattedDate);
  };

  const checkUseRestricted = () => {
    useRestricted.value = isBeforeBlockEnd();

    if (!useRestricted.value) {
      removeIntervalUseBlockChecker();
    }

    blockType === Block.BOOK
      ? userStore.setBookBlocked(useRestricted.value)
      : userStore.setBoardBlocked(useRestricted.value);

    updateBlockReleaseDisplay();
  };

  const intervalUseRestricted = ref();

  const setIntervalUseBlockChecker = () => {
    checkUseRestricted(); // 즉시 상태를 확인하고 업데이트

    if (!useRestricted.value) return;

    removeIntervalUseBlockChecker(); // 기존 인터벌 제거
    intervalUseRestricted.value = setInterval(
      checkUseRestricted,
      USE_RESTRICTED_CHECK_TIME_MILLIS
    );
  };

  const removeIntervalUseBlockChecker = () => {
    clearInterval(intervalUseRestricted.value);
  };

  watch(
    () => isApplicationActive,
    (isActive) => {
      isActive ? setIntervalUseBlockChecker() : removeIntervalUseBlockChecker();
    }
  );

  watch(blockEndDtm, (_) => {
    setIntervalUseBlockChecker();
  });

  return {
    setIntervalUseBlockChecker,
    removeIntervalUseBlockChecker,
  };
}
